import React, { useState, useEffect, useRef } from "react";

const FileItem = ({ item, getFileIcon, convertToBytes, onRemove, disabled }) => {
  const [progress, setProgress] = useState(item.file ? 0 : 100);
  const [status, setStatus] = useState(item.file ? "uploading" : "done");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let active = true;
    if (item.file && item.file.type.startsWith("image/")) {
      const url = URL.createObjectURL(item.file);
      if (active) setPreviewUrl(url);
      return () => {
        active = false;
        URL.revokeObjectURL(url);
      };
    }
  }, [item.file]);

  useEffect(() => {
    if (item.file && progress < 100) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setStatus("done");
            return 100;
          }
          const step = Math.floor(Math.random() * 15) + 15;
          const next = prev + step;
          if (next >= 100) {
            setStatus("done");
            return 100;
          }
          return next;
        });
      }, 120);
      return () => clearInterval(interval);
    }
  }, [item.file]);

  const fileTheme = getFileIcon(item.name);
  const isImage = (item.file && item.file.type.startsWith("image/")) || 
                  (item.name && /\.(jpg|jpeg|png|gif|svg)$/i.test(item.name));
  
  const imageUrl = previewUrl || item.url || item.filePath;
  const showPreview = isImage && imageUrl && !imageError;

  return (
    <div className="wf-file-card">
      <div className="wf-file-info" style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
        {showPreview ? (
          <div className="wf-file-preview-wrapper">
            <img 
              src={imageUrl} 
              alt={item.name} 
              className="wf-file-preview-img" 
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <div 
            className="wf-file-type-icon" 
            style={{ background: fileTheme.bg, color: fileTheme.color }}
          >
            <i className={`fa ${fileTheme.icon}`} aria-hidden="true" />
          </div>
        )}
        <div className="wf-file-meta">
          <span className="wf-file-name" title={item.name}>{item.name}</span>
          <div className="wf-file-details">
            <span className="wf-file-size">{convertToBytes(item.size)}</span>
            {status === "uploading" ? (
              <span className="wf-status-chip uploading">Uploading {progress}%</span>
            ) : (
              <span className="wf-status-chip done">Ready</span>
            )}
          </div>
          {status === "uploading" && (
            <div className="wf-progress-container">
              <div className="wf-progress-bar" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </div>
      <div className="wf-file-actions">
        {status === "done" && (
          <i className="fa fa-check-circle wf-success-check" aria-hidden="true" style={{ marginRight: "4px" }} />
        )}
        <button
          type="button"
          className="wf-file-action-btn delete"
          title="Remove File"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            !disabled && onRemove(item);
          }}
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1
          }}
        >
          <i className="fa fa-trash-o" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const UploadDocument = (props) => {
  const {
    documents,
    onFileChange,
    handleFileRemove,
    acceptedFile,
    btnLable,
    disabled,
  } = props;

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const convertToBytes = (x) => {
    const units = ["bytes", "KB", "MB"];
    let l = 0,
      n = parseInt(x, 10) || 0;

    while (n >= 1024 && ++l) {
      n = n / 1024;
    }

    return n.toFixed(n < 10 && l > 0 ? 1 : 0) + " " + units[l];
  };

  const getFileIcon = (filename) => {
    if (!filename) return { icon: "fa-file-text-o", color: "#64748b", bg: "#f8fafc" };
    const ext = filename.split(".").pop().toLowerCase();
    switch (ext) {
      case "pdf":
        return { icon: "fa-file-pdf-o", color: "#ef4444", bg: "#fef2f2" };
      case "doc":
      case "docx":
        return { icon: "fa-file-word-o", color: "#3b82f6", bg: "#eff6ff" };
      case "xls":
      case "xlsx":
        return { icon: "fa-file-excel-o", color: "#10b981", bg: "#ecfdf5" };
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "svg":
        return { icon: "fa-file-image-o", color: "#8b5cf6", bg: "#f5f3ff" };
      case "zip":
      case "rar":
      case "tar":
      case "gz":
        return { icon: "fa-file-archive-o", color: "#f97316", bg: "#fff7ed" };
      default:
        return { icon: "fa-file-text-o", color: "#64748b", bg: "#f8fafc" };
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    if (disabled) return;
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (disabled) return;
    setIsDragging(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      onFileChange({ target: { files: event.dataTransfer.files } });
      event.dataTransfer.clearData();
    }
  };

  const acceptedText = acceptedFile
    ? `Supported formats: ${acceptedFile.replace(/\./g, "").toUpperCase().split(",").join(", ")}`
    : "Supports all standard document types";

  return (
    <div className="wf-upload-container">
      <style>{`
        .wf-upload-container {
          width: 100%;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        
        /* Dropzone base */
        .wf-dropzone {
          position: relative;
          width: 100%;
          padding: 30px 20px;
          background: #ffffff;
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          text-align: center;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.01);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          --dropzone-icon-bg: #f1f5f9;
          --dropzone-icon-color: #64748b;
        }
        
        /* Dragging state */
        .wf-dropzone.is-dragging {
          border-color: #2563eb;
          background: rgba(37, 99, 235, 0.03);
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1), inset 0 2px 4px rgba(37, 99, 235, 0.02);
          transform: scale(0.995);
          --dropzone-icon-bg: rgba(37, 99, 235, 0.12);
          --dropzone-icon-color: #2563eb;
        }
        
        /* Hover state */
        .wf-dropzone:hover {
          border-color: #2563eb;
          background: #f8fafc;
          --dropzone-icon-bg: rgba(37, 99, 235, 0.08);
          --dropzone-icon-color: #2563eb;
        }
        
        .wf-dropzone.is-dragging:hover {
          border-color: #2563eb;
          background: rgba(37, 99, 235, 0.03);
        }
        
        /* SVG Animation */
        .wf-upload-svg {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          margin-bottom: 12px;
          flex-shrink: 0;
        }
        .wf-dropzone:hover .wf-upload-svg {
          transform: translateY(-2px);
        }
        .wf-dropzone.is-dragging .wf-upload-svg {
          transform: scale(1.05) translateY(-2px);
        }
        
        .wf-upload-title {
          font-size: 13.5px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 4px 0;
          line-height: 1.4;
        }
        
        .wf-upload-title .browse-link {
          color: #2563eb;
          text-decoration: underline;
          font-weight: 700;
        }
        
        .wf-upload-subtitle {
          font-size: 11px;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        .wf-browse-btn {
          margin-top: 12px;
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 600;
          color: #ffffff;
          background: #2563eb;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(37, 99, 235, 0.15);
        }
        
        .wf-dropzone:hover .wf-browse-btn {
          background: #1d4ed8;
          box-shadow: 0 4px 6px rgba(37, 99, 235, 0.25);
        }
        
        /* File List Stack */
        .wf-file-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 14px;
          width: 100%;
        }
        
        /* File card */
        .wf-file-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.02);
          transition: all 0.2s ease;
          animation: fileFadeIn 0.25s ease both;
        }
        .wf-file-card:hover {
          border-color: #cbd5e1;
          box-shadow: 0 4px 10px rgba(15, 23, 42, 0.04);
          transform: translateY(-1px);
        }
        
        @keyframes fileFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        
        /* File Preview / Thumbnail */
        .wf-file-preview-wrapper {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        }

        .wf-file-preview-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        /* File extension icon wrapper */
        .wf-file-type-icon {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          flex-shrink: 0;
        }
        
        .wf-file-meta {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
          text-align: left;
        }
        
        .wf-file-name {
          font-size: 12.5px;
          font-weight: 600;
          color: #334155;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }
        
        .wf-file-details {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
        }

        .wf-file-size {
          color: #64748b;
          font-weight: 500;
        }

        /* Status Chip */
        .wf-status-chip {
          padding: 1px 4px;
          border-radius: 4px;
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .wf-status-chip.uploading {
          background: #fef3c7;
          color: #d97706;
        }

        .wf-status-chip.done {
          background: #dcfce7;
          color: #15803d;
        }

        /* Progress Bar */
        .wf-progress-container {
          width: 100%;
          height: 3px;
          background: #f1f5f9;
          border-radius: 2px;
          margin-top: 4px;
          overflow: hidden;
        }

        .wf-progress-bar {
          height: 100%;
          background: #2563eb;
          border-radius: 2px;
          transition: width 0.15s ease-out;
        }
        
        /* Delete button */
        .wf-file-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        .wf-file-action-btn {
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          color: #ef4444;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          transition: all 0.2s ease;
        }
        .wf-file-action-btn.delete:hover {
          background: #fef2f2;
          color: #dc2626;
        }

        /* Animated success checkmark */
        .wf-success-check {
          color: #22c55e;
          font-size: 14px;
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }

        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        id="AdhocMessagedocName"
        type="file"
        style={{ display: "none" }}
        onChange={(event) => onFileChange(event)}
        name="files"
        multiple
        accept={acceptedFile}
      />

      {/* Drag & Drop Area */}
      <div
        className={`wf-dropzone ${isDragging ? "is-dragging" : ""} ${disabled ? "disabled" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current && fileInputRef.current.click()}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.65 : 1,
          pointerEvents: disabled ? "none" : "auto"
        }}
      >
        <svg className="wf-upload-svg" width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="32" cy="32" r="32" fill="var(--dropzone-icon-bg)" style={{ transition: "all 0.3s ease" }} />
          <path d="M32 18V38M32 18L25 25M32 18L39 25" stroke="var(--dropzone-icon-color)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "all 0.3s ease" }} />
          <path d="M46 32.5C48.5 34.5 50 37.5 50 41C50 46.5 45.5 51 40 51H22C16.5 51 12 46.5 12 41C12 36.5 15 32.5 19.5 31.5C19.8 24.5 25.5 19 32.5 19C37.5 19 41.8 21.8 44 26" stroke="var(--dropzone-icon-color)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "all 0.3s ease" }} />
        </svg>
        <h4 className="wf-upload-title">
          {isDragging ? "Drop your files now!" : <>Drag & drop files here, or <span className="browse-link">browse</span></>}
        </h4>
        <p className="wf-upload-subtitle">{acceptedText}</p>
        {!isDragging && (
          <button type="button" className="wf-browse-btn" disabled={disabled}>
            {btnLable || "Select Files"}
          </button>
        )}
      </div>

      {/* File List Stack */}
      {documents && documents.length > 0 && (
        <div className="wf-file-list">
          {documents.map((item, key) => (
            <FileItem
              key={key}
              item={item}
              getFileIcon={getFileIcon}
              convertToBytes={convertToBytes}
              onRemove={handleFileRemove}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default UploadDocument;
